import type { ResultSet } from '@projections/engine';
import { useMemo, useState } from 'react';
import { navigate } from '../../app/routes';
import { useAppStore } from '../../app/store';
import { EmptyState } from '../../components/EmptyState';
import { StatCard } from '../../components/StatCard';
import { formatCompactCurrency } from '../../design/format';
import { LedgerTable } from './LedgerTable';
import { describePath, pathTerminalWealth } from './ledgerView';
import { PathPicker } from './PathPicker';
import './ledger.css';

/**
 * LEDGER DRILL-DOWN (PLAN.md M7 screen 3) — "the I-can-debug-what's-inside promise surfaced in the
 * UI". Pick a path (by percentile or index), read its year-by-year ledger as a financial statement,
 * and expand any year into the engine's `explain(year)` breakdown.
 *
 * Reads the in-memory `ResultSet` from the store (every path's `Ledger` is retained). Before a run,
 * an EmptyState routes back to the editor. The selected path index is clamped to the current result
 * so a stale index from a previous, larger run can never index past the end.
 */
export function LedgerScreen(): React.ReactElement {
  const result = useAppStore((s) => s.result);
  const [pathIndex, setPathIndex] = useState(0);

  if (result === null) {
    return (
      <EmptyState
        title="Nothing to drill into yet"
        body="Run a simulation, then come back to read any path's ledger year by year — and expand a year to see the full accounting, like a bank statement."
        actionLabel="Go to plan"
        onAction={() => navigate('editor')}
      />
    );
  }

  const count = result.ledgers.length;
  const safeIndex = Math.min(Math.max(0, pathIndex), Math.max(0, count - 1));
  const ledger = result.ledgers[safeIndex];

  return (
    <div className="ledger reveal">
      <div className="ledger__header">
        <h1>Ledger</h1>
        <p className="ledger__subtitle">
          Read one simulated path like a bank statement. Pick a percentile or path, then click any
          year to expand its full accounting.
        </p>
        <PathPicker result={result} pathIndex={safeIndex} onPick={setPathIndex} />
      </div>

      {ledger !== undefined ? (
        <>
          <LedgerSummary result={result} pathIndex={safeIndex} />
          <LedgerTable ledger={ledger} />
        </>
      ) : (
        <p style={{ color: 'var(--neg)' }}>No ledger for path {safeIndex}.</p>
      )}
    </div>
  );
}

/** A small at-a-glance band for the selected path: terminal wealth, span, outcome. */
function LedgerSummary({
  result,
  pathIndex,
}: {
  result: ResultSet;
  pathIndex: number;
}): React.ReactElement | null {
  const { terminal, depleted, years, span } = useMemo(() => {
    const ledger = result.ledgers[pathIndex];
    if (ledger === undefined || ledger.length === 0) {
      return { terminal: 0, depleted: false, years: 0, span: '—' };
    }
    const first = ledger.get(0, 'period');
    const last = ledger.get(ledger.length - 1, 'period');
    const { depleted: dep } = describePath(result, pathIndex);
    return {
      terminal: pathTerminalWealth(ledger),
      depleted: dep,
      years: ledger.length,
      span: `${first}–${last}`,
    };
  }, [result, pathIndex]);

  return (
    <div className="ledger__summary reveal reveal--1">
      <StatCard
        label="Terminal wealth"
        value={formatCompactCurrency(terminal)}
        emphasis="display"
        tone={depleted ? 'neg' : 'default'}
      />
      <StatCard
        label="Outcome"
        value={depleted ? 'Depleted' : 'Funded'}
        tone={depleted ? 'neg' : 'pos'}
      />
      <StatCard label="Horizon" value={`${years} yr`} note={span} />
      <StatCard label="Path" value={`#${pathIndex}`} note={`of ${result.ledgers.length}`} />
    </div>
  );
}
